import { useRef, useState, type KeyboardEvent } from 'react';
import type { BoardDoc } from '../../doc/BoardDoc';
import { rotatedBounds, type Size } from '../../doc/geometry';
import type { FrameItem } from '../../doc/schema';
import { IconClose } from '../../ui/icons';
import { useBoard, useItemsVersion, usePane, usePaneStore, useReadOnly } from '../context';
import { frameContains } from '../frames';
import { readingOrder } from '../noteList';
import { fitRect, screenToWorld, type Camera } from '../viewport/camera';
import { flyTo } from '../viewport/flyTo';

export interface FrameRow {
  frame: FrameItem;
  /** How many frames it sits inside. */
  depth: number;
  /** Items inside it, not counting frames. */
  count: number;
}

const area = (f: FrameItem) => f.w * f.h;

/**
 * Every frame on the board as an outline: top-level frames in reading order (rows from the top,
 * left to right), each followed by the frames inside it. A frame's parent is the smallest frame
 * that holds it whole; ties go by id, so two frames on the same spot never hold each other.
 */
export function frameRows(board: BoardDoc): FrameRow[] {
  const frames = board.index.all().filter((i): i is FrameItem => i.type === 'frame');
  const outranks = (a: FrameItem, b: FrameItem) => area(a) > area(b) || (area(a) === area(b) && a.id < b.id);
  const children = new Map<string | null, FrameItem[]>();
  for (const f of frames) {
    let parent: FrameItem | null = null;
    for (const g of frames) {
      if (g === f || !outranks(g, f) || !frameContains(g, f)) continue;
      if (!parent || outranks(parent, g)) parent = g;
    }
    const key = parent?.id ?? null;
    const list = children.get(key);
    if (list) list.push(f);
    else children.set(key, [f]);
  }
  const rows: FrameRow[] = [];
  const walk = (key: string | null, depth: number) => {
    for (const frame of readingOrder(children.get(key) ?? [])) {
      const count = board.index.query(frame).filter((i) => i.type !== 'frame' && frameContains(frame, i)).length;
      rows.push({ frame, depth, count });
      walk(frame.id, depth + 1);
    }
  };
  walk(null, 0);
  return rows;
}

/** The innermost frame under the middle of the view, if any. */
function frameAtCentre(rows: FrameRow[], camera: Camera, size: Size): string | null {
  const c = screenToWorld(camera, { x: size.w / 2, y: size.h / 2 });
  let best: FrameRow | null = null;
  for (const r of rows) {
    const f = r.frame;
    if (c.x < f.x || c.x > f.x + f.w || c.y < f.y || c.y > f.y + f.h) continue;
    if (!best || r.depth > best.depth) best = r;
  }
  return best?.frame.id ?? null;
}

/** Brings a frame into view: fitted with a margin, no closer than 100 %, at the canvas's current rotation. */
export function flyToFrame(pane: ReturnType<typeof usePaneStore>, frame: FrameItem): void {
  const { camera, size } = pane.getState();
  flyTo(pane, fitRect(rotatedBounds(frame), size, 48, 1, camera.rot));
}

const titleOf = (f: FrameItem) => f.props.title.trim();

/**
 * The frame index: a docked column beside the canvas (a band under it on phones), styled like the
 * cutout drawer, listing the board's frames by title. A click flies there; a double-click
 * renames the frame; the frame in the middle of the view is marked as you move about.
 */
export function FrameIndex({ onClose }: { onClose: () => void }) {
  const board = useBoard();
  const pane = usePaneStore();
  const readOnly = useReadOnly();
  useItemsVersion();
  const rows = frameRows(board);
  const current = usePane((s) => frameAtCentre(rows, s.camera, s.size));
  const [filter, setFilter] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const q = filter.trim().toLowerCase();
  // filtered, the outline flattens: a match is shown whatever it sits in
  const shown = q ? rows.filter((r) => titleOf(r.frame).toLowerCase().includes(q)).map((r) => ({ ...r, depth: 0 })) : rows;

  const rename = (frame: FrameItem, title: string) => {
    setRenaming(null);
    if (title !== frame.props.title) board.updateItem(frame.id, { props: { title } });
  };

  // arrow keys walk the list, as in a menu
  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const buttons = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('.frame-index-go') ?? [])];
    if (!buttons.length) return;
    e.preventDefault();
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'ArrowDown' ? Math.min(buttons.length - 1, at + 1) : Math.max(0, at < 0 ? buttons.length - 1 : at - 1);
    buttons[next]?.focus();
  };

  return (
    <aside className="frame-index" onPointerDown={(e) => e.stopPropagation()} aria-label="Frames">
      <div className="insp-head frame-index-head">
        <span>Frames</span>
        <button type="button" className="panel-close" aria-label="Close the frame index" title="Close the frame index (J)" onClick={onClose}>
          <IconClose />
        </button>
      </div>
      {rows.length > 0 && (
        <div className="frame-index-filter">
          <input
            type="search"
            value={filter}
            placeholder="Filter frames"
            aria-label="Filter frames"
            spellCheck={false}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Escape') setFilter('');
              if (e.key === 'Enter' && shown[0]) flyToFrame(pane, shown[0].frame);
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                listRef.current?.querySelector<HTMLButtonElement>('.frame-index-go')?.focus();
              }
            }}
          />
        </div>
      )}
      <div className="frame-index-list" ref={listRef} role="list" onKeyDown={onListKey}>
        {rows.length === 0 ? (
          <div className="frame-index-note">No frames on this board yet. Draw one with the frame tool (F); its title shows up here.</div>
        ) : shown.length === 0 ? (
          <div className="frame-index-note">No frame title contains “{filter.trim()}”.</div>
        ) : (
          shown.map(({ frame, depth, count }) => {
            const title = titleOf(frame);
            return (
              <div
                key={frame.id}
                role="listitem"
                className={`frame-index-row${frame.id === current ? ' is-current' : ''}`}
                style={{ paddingLeft: 6 + depth * 14 }}
              >
                <span className="frame-index-swatch" style={frame.props.color ? { background: frame.props.color } : undefined} />
                {renaming === frame.id ? (
                  <input
                    className="frame-index-rename"
                    defaultValue={frame.props.title}
                    autoFocus
                    spellCheck={false}
                    aria-label="Frame title"
                    onFocus={(e) => e.currentTarget.select()}
                    onBlur={(e) => rename(frame, e.currentTarget.value.trim())}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') e.currentTarget.blur();
                      if (e.key === 'Escape') setRenaming(null);
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="frame-index-go"
                    title={readOnly ? 'Go to this frame' : 'Go to this frame (double-click to rename)'}
                    onClick={() => flyToFrame(pane, frame)}
                    onDoubleClick={() => {
                      if (!readOnly && !board.isLocked(frame)) setRenaming(frame.id);
                    }}
                  >
                    <span className={`frame-index-title${title ? '' : ' is-untitled'}`}>{title || 'Untitled frame'}</span>
                    <span className="frame-index-count" title={`${count} item${count === 1 ? '' : 's'} inside`}>
                      {count}
                    </span>
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
